import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Truck, ArrowRight, CheckCircle2, DollarSign, Wallet, ShieldCheck, AlertTriangle,
  Calculator, Lock, Sparkles, PiggyBank,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { applyPageSeo } from "@/lib/seo";
import { useToolGate } from "@/hooks/useToolGate";
import { AnonSignupLock, CommunityProNudge } from "@/components/ToolGate";

const PAGE_URL = "https://routehaulpathways.com/how-much-does-it-cost-to-start-a-trucking-company";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/2994820176cc397b772411ad081a2165912e5ceaa9cd86dd7c24ac93f37a0825.jpeg";
const TITLE = "How Much Does It Cost to Start a Trucking Company? (2026) | RouteHaul";
const DESCRIPTION =
  "How much does it cost to start a trucking company in 2026? A realistic breakdown of startup capital ($30k–$61k with your own authority): truck down payment, insurance, filing fees, and the cash reserve most new owner-operators underestimate.";

const FREE_LIMIT = 2;
const STORE_KEY = "rh_budget_free_uses_v1";

const money = (n) => `$${Math.round(isFinite(n) ? n : 0).toLocaleString()}`;
const num = (v) => parseFloat(v) || 0;

const COST_TABLE = [
  ["Truck down payment (10–20% financed)", "$5,000 – $26,000"],
  ["Insurance deposit (2–3 months upfront)", "$4,000 – $9,000"],
  ["Authority & filings (MC, BOC-3, UCR, IRP/IFTA)", "$1,200 – $2,600"],
  ["ELD, permits, plates & misc. setup", "$1,000 – $3,000"],
  ["Working capital / cash reserve", "$15,000 – $25,000"],
];

const CATEGORIES = [
  { icon: Truck, title: "The truck", text: "Your biggest line item. Financing needs a 10–20% down payment ($5k–$26k); buying outright pushes total needs to $60k–$120k+. Leasing onto a carrier first is a cheaper entry (~$15k–$20k) while you learn the numbers." },
  { icon: ShieldCheck, title: "Insurance", text: "A new authority's first-year premium commonly runs $15k–$35k, and insurers usually want 2–3 months upfront as a deposit. It's the cost that surprises most new owner-operators — budget for it before anything else." },
  { icon: DollarSign, title: "Authority & compliance filings", text: "One-time and annual government costs: MC authority ($300), BOC-3 ($25–$150), UCR (~$46), plus IRP plates and IFTA if you run interstate ($1,600–$2,600). Individually small, together a few thousand dollars." },
  { icon: PiggyBank, title: "Cash reserve (the one people skip)", text: "Brokers often pay in 30–45 days and may wait 90–180 days before booking a brand-new authority. Most successful startups hold $15k–$25k — several months of business AND personal expenses — so a slow start doesn't sink them." },
];

const FAQS = [
  { q: "How much does it cost to start a trucking company in 2026?", a: "With your own authority and a financed truck, plan for roughly $30,000–$61,000 to launch. That covers a truck down payment, insurance deposits, ~$1,200–$2,600 in filing fees, and — most importantly — a working-capital reserve. Buying a truck outright can push total needs to $60,000–$120,000+." },
  { q: "What's the single biggest startup cost besides the truck?", a: "Insurance. A new authority's first-year commercial premium commonly runs $15,000–$35,000, and insurers usually require 2–3 months upfront. Many new owner-operators budget for the truck but underestimate insurance." },
  { q: "How much cash reserve should I have before starting?", a: "Most successful new owner-operators start with $15,000–$25,000 in reserve — several months of both business and personal expenses. Brokers pay slowly (30–45 days) and may not book a brand-new authority for 90–180 days, so cash flow, not profit, is what sinks most startups early." },
  { q: "Is it cheaper to lease onto a carrier than get my own authority?", a: "Yes, upfront. Leasing on can start around $15,000–$20,000 because the carrier handles compliance, insurance and finding freight — but they take a cut and control your loads. Your own authority costs more and carries more responsibility, but you keep more of every load." },
  { q: "What are the one-time filing fees to get started?", a: "MC operating authority is $300, a BOC-3 process-agent filing runs $25–$150, UCR is about $46/year, and if you run interstate you'll add IRP plates and an IFTA license ($1,600–$2,600). Total government filing costs are usually about $1,200–$2,600." },
  { q: "How can I estimate my own startup budget?", a: "Use the free Startup Budget Planner on this page — enter your truck, insurance and reserve numbers to see your total cash-to-launch and recommended reserve. RouteHaul's free 12-Step guide then walks you through each cost in the right order." },
];

const Field = ({ id, label, value, onChange, prefix, suffix }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <Input id={id} data-testid={`budget-${id}`} type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} className={`h-11 font-mono tabular ${prefix ? "pl-7" : ""} ${suffix ? "pr-16" : ""}`} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>}
    </div>
  </div>
);

const RowLine = ({ label, value, highlight, muted, testid }) => (
  <div className="flex items-center justify-between">
    <span className={`text-sm ${muted ? "text-slate-400" : "text-slate-300"}`}>{label}</span>
    <span data-testid={testid} className={`font-mono tabular font-bold ${highlight ? "text-brand text-lg" : muted ? "text-slate-300" : "text-white"}`}>{value}</span>
  </div>
);

export default function StartupCost() {
  const { user, isPro, isCommunity, isAnon, locked, anonFreeLeft, registerUse, ready } = useToolGate(STORE_KEY);

  const [results, setResults] = useState(null);
  const [f, setF] = useState({
    downPayment: 12000, insurance: 6000, filings: 2000, misc: 1500,
    monthlyExpenses: 6000, reserveMonths: 3,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: "How much does it cost to start a trucking company — RouteHaul Logistics Pathways",
    seoKey: "startup",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: "How Much Does It Cost to Start a Trucking Company? (2026)",
          description: DESCRIPTION,
          about: "Cost to start a trucking company as an owner-operator",
          author: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          publisher: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          mainEntityOfPage: PAGE_URL,
          image: OG_IMAGE,
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      ],
    },
  }), []);

  const compute = () => {
    const upfront = num(f.downPayment) + num(f.insurance) + num(f.filings) + num(f.misc);
    const reserve = num(f.monthlyExpenses) * num(f.reserveMonths);
    const total = upfront + reserve;
    return { upfront, reserve, total };
  };

  const handleCalculate = () => {
    if (locked) return;
    setResults(compute());
    registerUse();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="startup-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="startup-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link to="/" data-testid="startup-dashboard"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Go to dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="startup-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
                <Link to="/register" data-testid="startup-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-5">
              <span className="w-8 h-px bg-brand" /> Owner-Operator Startup Costs
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[1.02]">
              How much does it cost to start a trucking company?
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-700">
              The 2026 reality: launching with your own authority and a financed truck usually takes
              <strong> $30,000–$61,000</strong> — and the piece most people underestimate isn't the truck,
              it's the cash reserve that carries you until brokers start paying.
            </p>
          </motion.div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="startup-quick-answer">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Quick answer
            </div>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              Expect <strong>$30,000–$61,000</strong> to start with your own authority on a financed truck:
              a truck down payment ($5k–$26k), insurance deposits ($4k–$9k), about <strong>$1,200–$2,600</strong> in
              filing fees, and a <strong>$15,000–$25,000</strong> working-capital reserve. Buying a truck outright
              can push the total to <strong>$60,000–$120,000+</strong>.
            </p>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href="#budget-planner" data-testid="startup-hero-cta">
              <Button className="h-13 px-7 py-3 rounded-full bg-brand hover:bg-brand-hover text-white font-bold group w-full sm:w-auto">
                Plan your startup budget
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Cost table */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 mb-3">Where the money goes</h2>
        <p className="text-slate-600 mb-8 max-w-2xl">A typical startup budget for a new authority on a financed truck:</p>
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          {COST_TABLE.map(([label, cost], i) => (
            <div key={label} data-testid={`startup-cost-row-${i}`} className={`flex items-baseline justify-between gap-3 px-6 py-4 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
              <span className="text-slate-700">{label}</span>
              <span className="font-mono font-bold text-slate-900 whitespace-nowrap">{cost}</span>
            </div>
          ))}
          <div className="flex items-baseline justify-between gap-3 px-6 py-4 bg-slate-900 text-white">
            <span className="font-bold">Typical total to launch</span>
            <span className="font-mono font-black text-brand whitespace-nowrap">$30,000 – $61,000</span>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black mb-10">The four buckets that make up your startup cost</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {CATEGORIES.map((c, i) => (
              <div key={c.title} data-testid={`startup-cat-${i}`} className="rounded-2xl border border-white/10 bg-white/5 p-7 flex gap-5">
                <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center shrink-0"><c.icon className="w-6 h-6 text-brand" strokeWidth={2.2} /></div>
                <div>
                  <h3 className="font-heading text-lg font-bold mb-1.5">{c.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Budget Planner tool (Pro-gated after 2 free runs) */}
      <section id="budget-planner" className="max-w-7xl mx-auto px-6 py-16 scroll-mt-20">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3 flex items-center gap-2"><Calculator className="w-4 h-4" /> Free tool</div>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900">Startup Budget Planner</h2>
        <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">Enter your own numbers to see your total cash-to-launch and the reserve you should have before your first load.</p>

        <div className="mt-8 grid lg:grid-cols-5 gap-8 items-start">
          {/* Inputs */}
          <div className="lg:col-span-3 space-y-8">
            <div className="border border-slate-200 rounded-md p-6">
              <div className="flex items-center gap-2 mb-5"><DollarSign className="w-4 h-4 text-brand" /><h3 className="font-heading text-lg font-semibold text-slate-900">Upfront costs to launch</h3></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="downPayment" label="Truck down payment" value={f.downPayment} onChange={set("downPayment")} prefix="$" />
                <Field id="insurance" label="Insurance deposit / first premium" value={f.insurance} onChange={set("insurance")} prefix="$" />
                <Field id="filings" label="Authority & filings (MC, BOC-3, UCR, IFTA)" value={f.filings} onChange={set("filings")} prefix="$" />
                <Field id="misc" label="ELD, permits, plates & misc." value={f.misc} onChange={set("misc")} prefix="$" />
              </div>
            </div>
            <div className="border border-slate-200 rounded-md p-6">
              <div className="flex items-center gap-2 mb-5"><Wallet className="w-4 h-4 text-brand" /><h3 className="font-heading text-lg font-semibold text-slate-900">Cash reserve</h3></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="monthlyExpenses" label="Monthly business + living expenses" value={f.monthlyExpenses} onChange={set("monthlyExpenses")} prefix="$" />
                <Field id="reserveMonths" label="Months of reserve to hold" value={f.reserveMonths} onChange={set("reserveMonths")} suffix="months" />
              </div>
            </div>

            {!locked ? (
              <div>
                <Button onClick={handleCalculate} data-testid="budget-calculate-btn" className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold w-full sm:w-auto group">
                  <Calculator className="w-5 h-5 mr-2" /> Calculate my startup budget
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                {ready && isAnon && anonFreeLeft > 0 && (
                  <p className="mt-3 text-sm text-slate-500" data-testid="budget-free-left">
                    1 free calculation — create a free account for unlimited use
                  </p>
                )}
                {isCommunity && results && (
                  <div className="mt-6"><CommunityProNudge testid="budget-pro-nudge" perk="save and revisit your plan" /></div>
                )}
              </div>
            ) : (
              <AnonSignupLock testid="budget-anon-lock" perks={[
                "Unlimited startup budget planning",
                "The full 12-Step Business Foundation Guide",
                "Every startup cost explained in the order you'll spend it",
              ]} />
            )}
          </div>

          {/* Results */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            {!results ? (
              <div className="border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="budget-empty">
                <Calculator className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Enter your numbers and hit <span className="font-bold text-slate-700">Calculate</span> to see your cash-to-launch.</p>
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900 text-white rounded-md p-8">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Total capital to start</div>
                <div className="font-mono font-bold tabular text-brand leading-none" data-testid="budget-total" style={{ fontSize: "clamp(3rem, 7vw, 4.5rem)" }}>{money(results.total)}</div>
                <div className="text-slate-500 text-sm mt-1">cash you should have before your first load</div>
                <div className="mt-8 space-y-3">
                  <RowLine label="Upfront to launch" value={money(results.upfront)} testid="budget-upfront" />
                  <RowLine label="Recommended cash reserve" value={money(results.reserve)} testid="budget-reserve" />
                  <div className="h-px bg-white/10 my-2" />
                  <RowLine label="Total capital needed" value={money(results.total)} highlight testid="budget-total-row" />
                </div>
                <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                  <AlertTriangle className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                  The reserve is what keeps you alive while brokers pay on 30–45 day terms — most carriers that fail run out of cash, not loads.
                </div>
              </motion.div>
            )}
            <p className="text-xs text-slate-400 mt-4 text-center">Estimates only — not financial advice.</p>
          </div>
        </div>
      </section>

      {/* Common mistake */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-7">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-amber-600" /><h2 className="font-heading text-xl font-black text-slate-900">The #1 way new carriers run out of money</h2></div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Undercapitalization. Most failed startups could drive — they just launched without enough reserve to
            survive slow-paying brokers and the 90–180 days before big brokers will book a brand-new authority.
            Budget for the wait, not just the launch, and you're already ahead of most.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Start with a real plan, not a guess</h2>
          <p className="mt-4 text-orange-50 text-lg">Create a free account for the 12-Step Business Foundation Guide — every startup cost explained in the order you'll actually spend it.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="startup-final-cta">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Start the free guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/how-to-get-your-own-authority" data-testid="startup-authority-cta">
              <Button variant="outline" className="h-14 px-8 rounded-full border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto">
                How to get your authority
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 text-center mb-10">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`stfaq-${i}`} data-testid={`startup-faq-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left font-heading font-bold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500">
            <ShieldCheck className="w-4 h-4" /> Educational mentorship — not legal, tax, or financial advice. Figures are general 2026 ranges.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/how-to-get-your-own-authority" className="hover:text-slate-900">Get Your Authority</Link>
            <Link to="/how-much-do-owner-operators-make" className="hover:text-slate-900">Owner-Operator Pay</Link>
            <Link to="/owner-operator-taxes" className="hover:text-slate-900">Taxes</Link>
            <Link to="/cost-per-mile-calculator" className="hover:text-slate-900">CPM Calculator</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
